import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useState, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  CheckCircle2,
  ChevronDown,
  ClipboardCheck,
  Globe2,
  Loader2,
  Search,
  ShieldCheck,
  Sparkles,
  Upload,
  UserRound,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { submitTherapistApplication, resendApplicantConfirmation } from "@/lib/therapist-applications.functions";
import { createApplicationUploadUrl } from "@/lib/therapist-uploads.functions";
import {
  US_STATE_OPTIONS,
  normaliseCountry,
  COUNTRY_OPTIONS,
  countryCodeFromName,
  countryNameFromCode,
} from "@/lib/therapist-licensing";

type UploadState =
  | { status: "idle" }
  | { status: "compressing"; fileName: string; sizeBytes: number }
  | { status: "uploading"; progress: number; fileName: string; sizeBytes: number }
  | { status: "done"; path: string; fileName: string; sizeBytes: number }
  | { status: "error"; message: string; fileName: string; sizeBytes: number; file: File; opts: UploadOpts };

type UploadOpts = {
  prefix: string;
  fallbackExt: string;
  isPhoto: boolean;
  maxBytes: number;
  acceptLabel: string;
};

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function friendlyUploadError(raw: string): string {
  const r = raw.toLowerCase();
  if (r.includes("stalled") || r.includes("timeout") || r.includes("timed out")) {
    return "Upload stalled — your connection may be slow. Try again on stronger Wi-Fi, or email the file to therapists@recoverytrauma.com.";
  }
  if (r.includes("network") || r.includes("failed to fetch")) {
    return "Network dropped during upload. Try again, or email the file to therapists@recoverytrauma.com.";
  }
  if (r.includes("too large") || r.includes("413") || r.includes("payload")) {
    return "File is too large. Please send a smaller version, or email it to therapists@recoverytrauma.com.";
  }
  // Never surface raw RLS / storage / database errors to therapists.
  return "We couldn't upload this file. Please try again, or email it to therapists@recoverytrauma.com.";
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

/** Downscale + recompress an image File in the browser to speed uploads. */
async function compressImage(
  file: File,
  { maxDim = 1600, quality = 0.82 }: { maxDim?: number; quality?: number } = {},
): Promise<File> {
  if (!file.type.startsWith("image/")) return file;
  if (file.type === "image/gif" || file.type === "image/svg+xml") return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();
    const blob: Blob | null = await new Promise((res) =>
      canvas.toBlob(res, "image/jpeg", quality),
    );
    if (!blob || blob.size >= file.size) return file;
    const base = file.name.replace(/\.[^.]+$/, "");
    return new File([blob], `${base}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
  } catch {
    return file;
  }
}

/** Upload a single file with progress via XHR using a short-lived signed URL. */
function uploadWithProgress(
  bucket: string,
  path: string,
  token: string,
  file: File,
  onProgress: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", `${SUPABASE_URL}/storage/v1/object/upload/sign/${bucket}/${path}?token=${encodeURIComponent(token)}`);
    xhr.setRequestHeader("apikey", SUPABASE_KEY);
    xhr.setRequestHeader("x-upsert", "false");
    if (file.type) xhr.setRequestHeader("Content-Type", file.type);

    xhr.timeout = 4 * 60 * 1000;

    let lastProgress = Date.now();
    let settled = false;
    const stallTimer = window.setInterval(() => {
      if (settled) return;
      if (Date.now() - lastProgress > 45_000) {
        settled = true;
        window.clearInterval(stallTimer);
        try { xhr.abort(); } catch { /* ignore */ }
        reject(new Error("Upload stalled — no progress for 45s"));
      }
    }, 5_000);
    const cleanup = () => {
      settled = true;
      window.clearInterval(stallTimer);
    };

    xhr.upload.onprogress = (evt) => {
      lastProgress = Date.now();
      if (evt.lengthComputable) onProgress(Math.round((evt.loaded / evt.total) * 100));
    };
    xhr.onload = () => {
      cleanup();
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(100);
        resolve();
      } else {
        let msg = `Upload failed (${xhr.status})`;
        try {
          const body = JSON.parse(xhr.responseText);
          if (body?.message) msg = body.message;
        } catch { /* ignore */ }
        reject(new Error(msg));
      }
    };
    xhr.onerror = () => { cleanup(); reject(new Error("Network error during upload")); };
    xhr.onabort = () => { cleanup(); reject(new Error("Upload cancelled")); };
    xhr.ontimeout = () => { cleanup(); reject(new Error("Upload timed out")); };
    xhr.send(file);
  });
}

export const Route = createFileRoute("/therapist-application")({
  head: () => ({
    meta: [
      { title: "Therapist Application — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Apply to be listed in the Recovery Trauma™ therapist directory. Trauma-informed practitioners only.",
      },
      { property: "og:title", content: "Therapist Application — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Apply to be listed in the Recovery Trauma™ therapist directory. Trauma-informed practitioners only.",
      },
      { property: "og:url", content: "https://recoverytrauma.com/therapist-application" },
      { property: "og:type", content: "website" },
      { name: "robots", content: "noindex,nofollow" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/therapist-application" }],
  }),
  component: TherapistApplicationPage,
});

const APPROACHES = [
  "EMDR",
  "IFS",
  "Somatic Experiencing",
  "Sensorimotor",
  "CBT",
  "DBT",
  "Polyvagal-Informed",
  "Art Therapy",
  "Psychodynamic",
  "Parts Work",
  "Brainspotting",
  "Other",
];
const SPECIALISMS = [
  "CPTSD",
  "PTSD",
  "Childhood Trauma",
  "Sexual Trauma",
  "Relational Trauma",
  "Grief & Loss",
  "Anxiety",
  "Dissociation",
  "Nervous System Regulation",
  "Attachment",
  "Depression",
  "Domestic Abuse",
  "Narcissistic Abuse",
  "Trauma Bonds",
  "Emotional Abuse",
  "Other",
];
const LANGUAGES = [
  "English",
  "Spanish",
  "French",
  "German",
  "Portuguese",
  "Arabic",
  "Mandarin",
  "Other",
];

const SUPPORT_EMAIL = "therapists@recoverytrauma.com";
const GENERIC_ERROR = `Your application could not be submitted. Please check your internet connection and try again. If the problem continues, contact ${SUPPORT_EMAIL}.`;

type MissingField = { key: string; label: string };

function mergeOther(list: string[], other: string): string[] {
  const base = list.filter((v) => v !== "Other");
  const extras = other
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  return Array.from(new Set([...base, ...extras]));
}

const STEPS = [
  { id: 1, title: "About You", blurb: "Who you are", icon: UserRound },
  { id: 2, title: "Credentials", blurb: "Licence & registration", icon: BadgeCheck },
  { id: 3, title: "Your Practice", blurb: "How you work", icon: Globe2 },
  { id: 4, title: "Verification", blurb: "Photo, certificate, insurance", icon: ShieldCheck },
  { id: 5, title: "Review & Submit", blurb: "Confirm and send", icon: ClipboardCheck },
] as const;

function TherapistApplicationPage() {
  const [submitted, setSubmitted] = useState(false);
  const [step, setStep] = useState(1);

  const [applicationId, setApplicationId] = useState<string | null>(null);
  const [resendState, setResendState] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [uploads, setUploads] = useState<Record<"photo" | "certificate" | "insurance", UploadState>>({
    photo: { status: "idle" },
    certificate: { status: "idle" },
    insurance: { status: "idle" },
  });
  const [missing, setMissing] = useState<MissingField[]>([]);
  const fieldRefs = useRef<Record<string, HTMLElement | null>>({});
  const topRef = useRef<HTMLDivElement | null>(null);
  const [form, setForm] = useState({
    full_name: "",
    professional_title: "",
    email: "",
    website: "",
    country: "",
    state_region: "",
    license_number: "",
    qualifications: "",
    years_experience: "",
    short_bio: "",
    insurance_provider: "",
    insurance_expiry: "",
    online: false,
    in_person: false,
    licensed_country: "",
    licence_type: "",
    licence_expiry: "",
    regulatory_body: "",
    regulatory_body_other: "",
    telehealth_permitted: false,
    licensed_country_other: "",
    // Three essential confirmations + optional marketing consent.
    confirm_accuracy: false,
    confirm_terms: false,
    confirm_publish: false,
    marketing_consent: false,
  });

  const [additionalJurisdictions, setAdditionalJurisdictions] = useState<string[]>([]);
  const [jurisdictionDraft, setJurisdictionDraft] = useState("");

  const [approaches, setApproaches] = useState<string[]>([]);
  const [approachesOther, setApproachesOther] = useState("");
  const [specialisms, setSpecialisms] = useState<string[]>([]);
  const [specialismsOther, setSpecialismsOther] = useState("");
  const [languages, setLanguages] = useState<string[]>([]);
  const [languagesOther, setLanguagesOther] = useState("");

  const [licensedStates, setLicensedStates] = useState<string[]>([]);

  const submitFn = useServerFn(submitTherapistApplication);
  const resendFn = useServerFn(resendApplicantConfirmation);

  const [resendAttempts, setResendAttempts] = useState(0);
  const [resendSuccesses, setResendSuccesses] = useState(0);
  const [resendFailures, setResendFailures] = useState(0);
  const [resendError, setResendError] = useState<string | null>(null);

  async function handleResendConfirmation() {
    if (!applicationId || resendState === "sending") return;
    const attemptNumber = resendAttempts + 1;
    setResendAttempts(attemptNumber);
    setResendError(null);
    setResendState("sending");
    const startedAt = Date.now();
    console.log("[analytics:therapist_resend_click]", {
      application_id: applicationId,
      attempt_number: attemptNumber,
      at: new Date().toISOString(),
    });
    try {
      await resendFn({ data: { application_id: applicationId } });
      const successCount = resendSuccesses + 1;
      setResendSuccesses(successCount);
      setResendState("sent");
      console.log("[analytics:therapist_resend_outcome]", {
        application_id: applicationId,
        attempt_number: attemptNumber,
        outcome: "success",
        elapsed_ms: Date.now() - startedAt,
        total_attempts: attemptNumber,
        total_successes: successCount,
        total_failures: resendFailures,
      });
      toast.success("Confirmation email resent. Please check your inbox and spam folder.");
    } catch (e) {
      const failureCount = resendFailures + 1;
      setResendFailures(failureCount);
      const errMsg = e instanceof Error ? e.message : String(e);
      console.error("[therapist-application:resend]", e);
      console.log("[analytics:therapist_resend_outcome]", {
        application_id: applicationId,
        attempt_number: attemptNumber,
        outcome: "failure",
        elapsed_ms: Date.now() - startedAt,
        error_message: errMsg,
        total_attempts: attemptNumber,
        total_successes: resendSuccesses,
        total_failures: failureCount,
      });
      setResendError(errMsg);
      setResendState("error");
      toast.error(`Could not resend confirmation email. Please email ${SUPPORT_EMAIL} if it doesn't arrive.`);
    }
  }

  const UPLOAD_CONFIGS: Record<"photo" | "certificate" | "insurance", UploadOpts> = {
    photo: { prefix: "photos/", fallbackExt: "jpg", isPhoto: true, maxBytes: 5 * 1024 * 1024, acceptLabel: "JPG, PNG, WebP – max 5 MB" },
    certificate: { prefix: "certificates/", fallbackExt: "pdf", isPhoto: false, maxBytes: 10 * 1024 * 1024, acceptLabel: "PDF, JPG, PNG – max 10 MB" },
    insurance: { prefix: "insurance/", fallbackExt: "pdf", isPhoto: false, maxBytes: 10 * 1024 * 1024, acceptLabel: "PDF, JPG, PNG – max 10 MB" },
  };

  async function startUpload(
    key: "photo" | "certificate" | "insurance",
    file: File,
    opts: UploadOpts,
  ) {
    if (file.size > opts.maxBytes) {
      toast.error(`File too large. Maximum ${formatBytes(opts.maxBytes)}.`);
      return;
    }
    setUploads((u) => ({ ...u, [key]: { status: "compressing", fileName: file.name, sizeBytes: file.size } }));
    try {
      const optimised = opts.isPhoto
        ? await compressImage(file, { maxDim: 1200, quality: 0.82 })
        : file.type.startsWith("image/")
          ? await compressImage(file, { maxDim: 2200, quality: 0.85 })
          : file;
      const ext = (optimised.name.split(".").pop() || opts.fallbackExt).toLowerCase();
      const signed = await createApplicationUploadUrl({
        data: { kind: opts.prefix.replace(/\/$/, "") as "photos" | "certificates" | "insurance", extension: ext },
      });
      const path = signed.path;
      setUploads((u) => ({
        ...u,
        [key]: { status: "uploading", progress: 0, fileName: optimised.name, sizeBytes: optimised.size },
      }));
      await uploadWithProgress("therapist-applications", path, signed.token, optimised, (pct: number) =>
        setUploads((u) => {
          const cur = u[key];
          if (cur.status !== "uploading") return u;
          return { ...u, [key]: { ...cur, progress: pct } };
        }),
      );
      setUploads((u) => ({
        ...u,
        [key]: { status: "done", path, fileName: optimised.name, sizeBytes: optimised.size },
      }));
    } catch (e) {
      const raw = e instanceof Error ? e.message : "Upload failed";
      console.error(`[upload:${key}]`, raw);
      setUploads((u) => ({
        ...u,
        [key]: {
          status: "error",
          message: friendlyUploadError(raw),
          fileName: file.name,
          sizeBytes: file.size,
          file,
          opts,
        },
      }));
    }
  }

  function clearUpload(key: "photo" | "certificate" | "insurance") {
    setUploads((u) => ({ ...u, [key]: { status: "idle" } }));
  }

  /** Required fields per step — verification requirements are unchanged. */
  function validateStep(n: number): MissingField[] {
    const m: MissingField[] = [];
    if (n === 1) {
      if (!form.full_name.trim()) m.push({ key: "full_name", label: "Full name" });
      if (!form.professional_title.trim())
        m.push({ key: "professional_title", label: "Professional title" });
      if (!form.email.trim()) m.push({ key: "email", label: "Email address" });
      if (!form.country.trim()) m.push({ key: "country", label: "Country / region" });
      if (!form.short_bio.trim() || form.short_bio.trim().length < 20)
        m.push({ key: "short_bio", label: "Short bio (at least 20 characters)" });
    }
    if (n === 2) {
      if (!form.licensed_country ||
          (form.licensed_country === "OTHER" && !form.licensed_country_other.trim()))
        m.push({ key: "licensed_country", label: "Country of practice" });
      if (!form.licence_type.trim())
        m.push({ key: "licence_type", label: "Licence / credential type" });
      if (!form.regulatory_body.trim() ||
          (form.regulatory_body === "Other" && !form.regulatory_body_other.trim()))
        m.push({ key: "regulatory_body", label: "Regulatory body" });
      if (!form.licence_expiry) m.push({ key: "licence_expiry", label: "Licence expiry date" });
      if (form.licensed_country === "US" && licensedStates.length === 0)
        m.push({ key: "licensed_states", label: "Licensed US state(s)" });
    }
    if (n === 3) {
      if (!form.website.trim())
        m.push({ key: "website", label: "Booking or contact link" });
    }
    if (n === 4) {
      if (!form.insurance_provider.trim())
        m.push({ key: "insurance_provider", label: "Insurance provider" });
      if (!form.insurance_expiry)
        m.push({ key: "insurance_expiry", label: "Insurance policy expiry date" });
    }
    if (n === 5) {
      if (!form.confirm_accuracy)
        m.push({ key: "confirm_accuracy", label: "Confirm accuracy, licensing, insurance and legal authority" });
      if (!form.confirm_terms)
        m.push({ key: "confirm_terms", label: "Agree to the Therapist Membership Terms & Privacy Policy" });
      if (!form.confirm_publish)
        m.push({ key: "confirm_publish", label: "Consent to your approved profile being published" });
    }
    return m;
  }

  const mutation = useMutation({
    mutationFn: async () => {
      const rawPrimary =
        form.licensed_country === "OTHER"
          ? form.licensed_country_other
          : form.licensed_country || form.country;
      const licensedCountryNorm = normaliseCountry(rawPrimary) || rawPrimary.trim();
      const allowed = additionalJurisdictions
        .map((c) => normaliseCountry(c.trim()) || c.trim())
        .filter(Boolean)
        .filter((c) => c !== licensedCountryNorm);
      const additional_jurisdictions = allowed.map((country) => ({
        country,
        states: [],
        regulatory_body: "",
        licence_number: "",
        licence_expiry: "",
      }));

      const photoState = uploads.photo;
      const certState = uploads.certificate;
      const insState = uploads.insurance;
      if ([photoState, certState, insState].some((s) => s.status === "uploading" || s.status === "compressing")) {
        throw new Error("Files are still uploading. Please wait for all uploads to finish.");
      }
      const photo_url = photoState.status === "done" ? photoState.path : "";
      const certificate_url = certState.status === "done" ? certState.path : "";
      const insurance_certificate_url = insState.status === "done" ? insState.path : "";

      return submitFn({
        data: {
          full_name: form.full_name,
          professional_title: form.professional_title,
          email: form.email,
          website: form.website,
          country: form.country,
          state_region: form.state_region,
          licensing_jurisdiction: (form.regulatory_body === "Other"
            ? form.regulatory_body_other
            : form.regulatory_body),
          license_number: form.license_number,
          professional_body: "",
          qualifications: form.qualifications,
          years_experience: form.years_experience ? Number(form.years_experience) : undefined,
          therapy_approaches: mergeOther(approaches, approachesOther),
          specialisms: mergeOther(specialisms, specialismsOther),
          languages: mergeOther(languages, languagesOther),
          availability_modes: [
            ...(form.online ? ["Online" as const] : []),
            ...(form.in_person ? ["In-person" as const] : []),
          ],
          short_bio: form.short_bio,
          photo_url,
          certificate_url,
          insurance_certificate_url,
          // Insurance + licensing authority are covered by the accuracy confirmation.
          insurance_confirmed: form.confirm_accuracy,
          insurance_provider: form.insurance_provider,
          insurance_expiry: form.insurance_expiry,
          consent_to_list: true as const,
          licensed_country: licensedCountryNorm,
          licensed_states: licensedStates,
          licence_type: form.licence_type,
          licence_expiry: form.licence_expiry,
          regulatory_body: (form.regulatory_body === "Other"
            ? form.regulatory_body_other
            : form.regulatory_body),
          telehealth_permitted: form.telehealth_permitted,
          additional_jurisdictions,
          consent_privacy_policy: true as const,
          consent_processing: true as const,
          consent_accurate: true as const,
          consent_no_guarantee: true as const,
          consent_clinical_director: true as const,
          consent_public_listing: true as const,
          consent_contact: true as const,
          confirm_professional_authority: true as const,
          consent_solely_responsible: true as const,
          consent_platform_not_responsible: true as const,
          consent_keep_updated: true as const,
          consent_notify_changes: true as const,
          consent_external_links: true as const,
          consent_directory_agreement: true as const,
          marketing_consent: form.marketing_consent,
        },
      });
    },
    onSuccess: (res) => {
      setMissing([]);
      setApplicationId((res as { application_id?: string })?.application_id ?? null);
      setSubmitted(true);
    },
    onError: (err) => {
      console.error("[therapist-application:submit]", err);
      toast.error(GENERIC_ERROR, { duration: 8000 });
    },
  });

  function toggle(list: string[], v: string, setter: (n: string[]) => void) {
    setter(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  }

  function focusFirstMissing(m: MissingField[]) {
    const first = fieldRefs.current[m[0].key];
    if (first) {
      first.scrollIntoView({ behavior: "smooth", block: "center" });
      const focusable = first.querySelector<HTMLElement>("input,select,textarea,button");
      focusable?.focus({ preventScroll: true });
    }
  }

  function goToStep(n: number) {
    setMissing([]);
    setStep(n);
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function handleNext() {
    const m = validateStep(step);
    setMissing(m);
    if (m.length > 0) {
      focusFirstMissing(m);
      return;
    }
    goToStep(Math.min(5, step + 1));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (step < 5) {
      handleNext();
      return;
    }
    // Re-check every step so nothing skipped slips through.
    for (let n = 1; n <= 5; n++) {
      const m = validateStep(n);
      if (m.length > 0) {
        setMissing(m);
        if (n !== step) {
          setStep(n);
          topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
          focusFirstMissing(m);
        }
        toast.error(`Please complete: ${m.map((x) => x.label).join(", ")}`, { duration: 8000 });
        return;
      }
    }
    setMissing([]);
    mutation.mutate();
  }

  if (submitted) {
    return (
      <div className="min-h-dvh bg-background py-16">
        <div className="mx-auto max-w-2xl rounded-2xl border border-gold/30 bg-card p-10 text-center">
          <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" />
          <h1 className="mt-4 text-3xl font-serif text-foreground">Application received</h1>
          <p className="mt-3 text-muted-foreground">
            Thank you. Your therapist application has been submitted successfully and saved to our
            review queue.
          </p>

          <div className="mt-5 rounded-xl border border-amber-400/40 bg-amber-50/60 p-4 text-start dark:bg-amber-950/20">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
              <div className="flex-1 text-sm text-foreground">
                <p className="font-medium">Confirmation email is best-effort</p>
                <p className="mt-1 text-muted-foreground">
                  We attempted to send a confirmation to{" "}
                  <span className="font-medium text-foreground">{form.email}</span>. Delivery can
                  occasionally fail or be delayed by spam filters — your application is safely
                  recorded either way. If it doesn't arrive within a few minutes, use the button
                  below or email{" "}
                  <a
                    href={`mailto:${SUPPORT_EMAIL}`}
                    className="font-medium text-[var(--gold-deep)] underline"
                  >
                    {SUPPORT_EMAIL}
                  </a>
                  .
                </p>
                <button
                  type="button"
                  onClick={handleResendConfirmation}
                  disabled={!applicationId || resendState === "sending"}
                  className="mt-3 inline-flex items-center gap-2 rounded-lg border border-gold/40 bg-gold/10 px-3 py-1.5 text-sm font-medium text-foreground hover:bg-gold/20 disabled:opacity-60"
                >
                  {resendState === "sending" ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Resending…
                    </>
                  ) : resendState === "sent" ? (
                    <>
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Email resent
                    </>
                  ) : resendState === "error" ? (
                    "Try resending again"
                  ) : (
                    "Resend confirmation email"
                  )}
                </button>
                {resendState === "sent" && (
                  <div
                    role="status"
                    aria-live="polite"
                    className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900"
                  >
                    <p className="font-medium">
                      <CheckCircle2 className="me-1 inline h-3.5 w-3.5" /> Confirmation email sent to {form.email}.
                    </p>
                    <p className="mt-1 text-emerald-800">
                      Still nothing in a few minutes? Check your spam folder, then contact{" "}
                      <a href={`mailto:${SUPPORT_EMAIL}`} className="underline">
                        {SUPPORT_EMAIL}
                      </a>
                      .
                    </p>
                  </div>
                )}
                {resendState === "error" && (
                  <div
                    role="alert"
                    aria-live="assertive"
                    className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900"
                  >
                    <p className="font-medium">
                      <AlertCircle className="me-1 inline h-3.5 w-3.5" /> We couldn't resend the confirmation email.
                    </p>
                    {resendError && (
                      <p className="mt-1 break-words text-red-800">Reason: {resendError}</p>
                    )}
                    <p className="mt-1 text-red-800">
                      Please try again, or email{" "}
                      <a href={`mailto:${SUPPORT_EMAIL}`} className="underline">
                        {SUPPORT_EMAIL}
                      </a>{" "}
                      and we'll confirm your application manually.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <p className="mt-4 text-sm text-muted-foreground">
            Our team will review your credentials and respond within 7–10 days. Recovery Trauma™
            provides a directory only and does not provide, manage, or supervise therapy.
          </p>
          <Link
            to="/therapists"
            className="mt-6 inline-flex items-center gap-2 rounded-lg border border-gold/40 bg-gold/10 px-4 py-2 text-sm font-medium text-foreground hover:bg-gold/20"
          >
            Back to directory
          </Link>
        </div>
      </div>
    );
  }

  const missingKeys = new Set(missing.map((m) => m.key));
  const baseInput =
    "w-full rounded-xl border bg-background px-3.5 py-2.5 text-sm text-foreground focus:outline-none";
  const inputCls = (key?: string) =>
    `${baseInput} ${
      key && missingKeys.has(key)
        ? "border-destructive ring-2 ring-destructive/40 focus:border-destructive"
        : "border-gold/30 focus:border-gold"
    }`;
  const labelCls = "mb-1.5 block text-sm font-medium text-foreground";
  const hintCls = "mt-1.5 text-xs leading-relaxed text-muted-foreground";
  const uploadCls = (key: string) =>
    `flex cursor-pointer items-center gap-2 rounded-xl border border-dashed px-4 py-3 text-sm text-muted-foreground hover:bg-gold/5 ${
      missingKeys.has(key)
        ? "border-destructive bg-destructive/5 ring-2 ring-destructive/40"
        : "border-gold/40 bg-background"
    }`;
  const setRef = (key: string) => (el: HTMLElement | null) => {
    fieldRefs.current[key] = el;
  };

  const isSubmitting = mutation.isPending;
  const isUploadingAny = (["photo", "certificate", "insurance"] as const).some(
    (k) => uploads[k].status === "uploading" || uploads[k].status === "compressing",
  );

  const primaryCountryLabel =
    form.licensed_country === "OTHER"
      ? form.licensed_country_other
      : countryNameFromCode(form.licensed_country) || form.licensed_country;

  return (
    <div className="min-h-dvh bg-background py-10 sm:py-14">
      <div ref={topRef} className="mx-auto max-w-3xl px-4">
        {/* Hero */}
        <header className="mb-8 text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-gold/40 bg-gold-light/40 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-[var(--gold-deep)]">
            <Sparkles className="h-3.5 w-3.5" /> Founding Therapist Membership
          </span>
          <h1 className="mt-4 font-serif text-3xl tracking-tight text-foreground md:text-4xl">
            Join the Recovery Trauma™ Therapist Directory
          </h1>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground">
            Five short steps, around five minutes. Applying is free — membership is only
            requested if your application is approved.
          </p>

          <div className="mt-6 grid gap-3 sm:grid-cols-3">
            {[
              { icon: ClipboardCheck, title: "Free to apply", body: "No payment at application stage." },
              { icon: ShieldCheck, title: "Document verification", body: "We review specified professional documents before a profile can be listed." },
              { icon: Globe2, title: "Global reach", body: "Seen by our 380,000+ community." },
            ].map((c) => (
              <div
                key={c.title}
                className="rounded-2xl border border-gold/25 bg-card p-4 text-start shadow-[var(--shadow-soft)]"
              >
                <c.icon className="h-5 w-5 text-[var(--gold-deep)]" />
                <p className="mt-2 text-sm font-semibold text-foreground">{c.title}</p>
                <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{c.body}</p>
              </div>
            ))}
          </div>
        </header>

        {/* Short summaries with Learn more */}
        <div className="mb-8 space-y-3">
          <Disclosure
            title="Recovery Trauma™ is a directory — not an employer"
            summary="We list independent therapists. We don't provide therapy, supervise clinical work, manage appointments or take a cut of your fees."
          >
            <p>
              Recovery Trauma™ provides a professional therapist directory only. We do not employ
              therapists, provide therapy, supervise clinical services, manage appointments, or
              process payments between therapists and clients. All therapy services are provided
              independently by each therapist.
            </p>
            <p className="mt-2">
              Before a profile can be listed, we carry out document verification only — we may review
              identity information, relevant qualification or certification documents, professional
              registration or membership where applicable, and evidence of current professional
              indemnity insurance. This is an administrative document check, not a clinical
              assessment. Recovery Trauma Ltd does not employ, supervise, clinically assess,
              recommend, endorse or guarantee therapists or their services, and listing is never
              clinical approval or endorsement. Submitting an application does not guarantee
              listing.
            </p>
          </Disclosure>

          <Disclosure
            title="Your booking link takes clients off-platform"
            summary="Your profile links to your own website or booking page. Clients arrange and pay for sessions directly with you."
          >
            <p>
              Recovery Trauma™ may link to external therapist websites, booking systems, or
              professional profiles for convenience. Recovery Trauma™ does not control or endorse
              the content, availability, privacy practices, pricing, or services provided on
              external websites. Clients are responsible for reviewing the therapist's own terms,
              privacy policy, fees, and booking conditions before engaging their services.
            </p>
          </Disclosure>

          <Disclosure
            title="How we handle your information"
            summary="Your application is used only to review your eligibility, and is visible only to authorised reviewers."
          >
            <p>
              Recovery Trauma™ collects the information in this application solely to review your
              eligibility to join the Therapist Directory, processed securely and in line with our{" "}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[var(--gold-deep)] underline">Privacy Policy</a>,{" "}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[var(--gold-deep)] underline">Terms &amp; Conditions</a> and{" "}
              <a href="/therapist-terms" target="_blank" rel="noopener noreferrer" className="text-[var(--gold-deep)] underline">Therapist Membership Terms</a>,
              including UK GDPR and GDPR where applicable. For your audit trail we securely record
              the date and time of consent, the IP address associated with this submission (where
              legally permitted), the policy versions accepted, and your marketing preference.
            </p>
          </Disclosure>

          <Disclosure
            title="How much does this cost?"
            summary="Applying is free. If you're approved, a listing is $69 USD per year — cancel anytime."
          >
            <p>
              There is no charge to apply and no charge while your application is reviewed. If
              you're approved, your directory listing is <strong>$69 USD per year</strong>, billed
              yearly and cancellable at any time. Your listing only goes live once you choose to
              activate it.
            </p>
            <p className="mt-2">
              Recovery Trauma™ never takes a commission or percentage of your session fees. Clients
              arrange and pay for therapy directly with you.
            </p>
          </Disclosure>
        </div>


        {/* Progress */}
        <div className="mb-6">
          <div className="flex items-center justify-between text-sm">
            <p className="font-semibold text-foreground">
              Step {step} of 5 — {STEPS[step - 1].title}
            </p>
            <p className="text-xs text-muted-foreground">{Math.round((step / 5) * 100)}% complete</p>
          </div>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gold/15">
            <div
              className="h-full rounded-full bg-[var(--gradient-gold,var(--gold-deep))] transition-[width] duration-300"
              style={{ width: `${(step / 5) * 100}%`, backgroundColor: "var(--gold-deep)" }}
            />
          </div>
          <ol className="mt-3 flex flex-wrap gap-1.5">
            {STEPS.map((s) => {
              const done = s.id < step;
              const active = s.id === step;
              return (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => (s.id < step ? goToStep(s.id) : undefined)}
                    disabled={s.id > step}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                      active
                        ? "border-gold bg-gold/20 text-foreground"
                        : done
                          ? "border-gold/40 bg-background text-foreground/80 hover:bg-gold/10"
                          : "border-border bg-background text-muted-foreground"
                    }`}
                  >
                    {done ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <s.icon className="h-3.5 w-3.5" />}
                    {s.title}
                  </button>
                </li>
              );
            })}
          </ol>
        </div>

        {missing.length > 0 && (
          <div
            role="alert"
            className="mb-6 rounded-xl border border-destructive/40 bg-destructive/10 p-4 text-sm text-foreground"
          >
            <div className="flex items-start gap-2">
              <AlertCircle className="mt-0.5 h-5 w-5 text-destructive" />
              <div>
                <p className="font-semibold text-destructive">Just a few things to complete:</p>
                <ul className="mt-2 list-disc space-y-1 ps-5">
                  {missing.map((m) => (
                    <li key={m.key}>{m.label}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          className="rounded-3xl border border-gold/20 bg-card p-5 shadow-[var(--shadow-soft)] sm:p-8"
        >
          <datalist id="country-options-list">
            {COUNTRY_OPTIONS.map((c) => (
              <option key={c.code} value={c.name} />
            ))}
          </datalist>

          {/* ───────────── Step 1: About You ───────────── */}
          {step === 1 && (
            <StepShell
              icon={UserRound}
              title="About you"
              blurb="The basics we'll show on your public profile."
            >
              <div className="grid gap-5 md:grid-cols-2">
                <div ref={setRef("full_name")}>
                  <label htmlFor="ta_full_name" className={labelCls}>Full name</label>
                  <input
                    id="ta_full_name"
                    name="full_name"
                    autoComplete="name"
                    className={inputCls("full_name")}
                    value={form.full_name}
                    onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  />
                </div>
                <div ref={setRef("professional_title")}>
                  <label htmlFor="ta_professional_title" className={labelCls}>Professional title</label>
                  <input
                    id="ta_professional_title"
                    name="professional_title"
                    className={inputCls("professional_title")}
                    placeholder="e.g. Trauma Therapist"
                    value={form.professional_title}
                    onChange={(e) => setForm({ ...form, professional_title: e.target.value })}
                  />
                </div>
                <div ref={setRef("email")}>
                  <label htmlFor="ta_email" className={labelCls}>Email</label>
                  <input
                    id="ta_email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    className={inputCls("email")}
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                  <p className={hintCls}>We only use this to contact you about your application.</p>
                </div>
                <div>
                  <label className={labelCls}>Years of experience</label>
                  <input
                    type="number"
                    min={0}
                    max={80}
                    className={inputCls()}
                    value={form.years_experience}
                    onChange={(e) => setForm({ ...form, years_experience: e.target.value })}
                  />
                </div>
                <div ref={setRef("country")}>
                  <label className={labelCls}>Country</label>
                  <input
                    list="country-options-list"
                    className={inputCls("country")}
                    placeholder="Start typing your country…"
                    value={form.country}
                    onChange={(e) => setForm({ ...form, country: e.target.value })}
                  />
                </div>
                <div>
                  <label className={labelCls}>State / region <span className="font-normal text-muted-foreground">(optional)</span></label>
                  <input
                    className={inputCls()}
                    value={form.state_region}
                    onChange={(e) => setForm({ ...form, state_region: e.target.value })}
                  />
                </div>
                <div ref={setRef("short_bio")} className="md:col-span-2">
                  <label className={labelCls}>Short bio</label>
                  <textarea
                    minLength={20}
                    maxLength={2000}
                    rows={5}
                    className={inputCls("short_bio")}
                    placeholder="A few warm sentences about your trauma-informed approach and who you work with."
                    value={form.short_bio}
                    onChange={(e) => setForm({ ...form, short_bio: e.target.value })}
                  />
                  <p className={hintCls}>{form.short_bio.trim().length}/2000 characters · minimum 20.</p>
                </div>
              </div>
            </StepShell>
          )}

          {/* ───────────── Step 2: Credentials ───────────── */}
          {step === 2 && (
            <StepShell
              icon={BadgeCheck}
              title="Professional credentials"
              blurb="Used for verification. We only show your profile to people you're legally permitted to support."
            >
              <div className="grid gap-5 md:grid-cols-2">
                <div>
                  <label className={labelCls}>Licence / registration number</label>
                  <input
                    className={inputCls()}
                    value={form.license_number}
                    onChange={(e) => setForm({ ...form, license_number: e.target.value })}
                  />
                </div>
                <div ref={setRef("licence_type")}>
                  <label className={labelCls}>Licence / credential type</label>
                  <input
                    className={inputCls("licence_type")}
                    placeholder="e.g. LMFT, LCSW, HCPC"
                    value={form.licence_type}
                    onChange={(e) => setForm({ ...form, licence_type: e.target.value })}
                  />
                </div>
                <div ref={setRef("regulatory_body")}>
                  <label className={labelCls}>Regulatory body</label>
                  <select
                    className={inputCls("regulatory_body")}
                    value={form.regulatory_body}
                    onChange={(e) => setForm({ ...form, regulatory_body: e.target.value })}
                  >
                    <option value="">Select regulatory body…</option>
                    <option value="BACP">BACP (UK)</option>
                    <option value="NCPS">NCPS (UK)</option>
                    <option value="HCPC">HCPC (UK)</option>
                    <option value="BABCP">BABCP (UK)</option>
                    <option value="UKCP">UKCP (UK)</option>
                    <option value="ACA">ACA (Australia)</option>
                    <option value="APA">APA (USA)</option>
                    <option value="Other">Other</option>
                  </select>
                  {form.regulatory_body === "Other" && (
                    <input
                      className={`${inputCls()} mt-2`}
                      placeholder="Please specify your regulatory body"
                      value={form.regulatory_body_other}
                      onChange={(e) => setForm({ ...form, regulatory_body_other: e.target.value })}
                    />
                  )}
                </div>
                <div ref={setRef("licence_expiry")}>
                  <label className={labelCls}>Licence expiry date</label>
                  <input
                    type="date"
                    className={inputCls("licence_expiry")}
                    value={form.licence_expiry}
                    onChange={(e) => setForm({ ...form, licence_expiry: e.target.value })}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className={labelCls}>Qualifications</label>
                  <textarea
                    className={inputCls()}
                    rows={2}
                    placeholder="List your relevant qualifications"
                    value={form.qualifications}
                    onChange={(e) => setForm({ ...form, qualifications: e.target.value })}
                  />
                </div>
              </div>

              <div className="mt-6 rounded-2xl border border-gold/25 bg-background/60 p-4 sm:p-5">
                <h3 className="text-sm font-semibold text-foreground">Where you're permitted to practise</h3>
                <p className={hintCls}>
                  Please be accurate — you remain responsible for being appropriately licensed,
                  registered and insured in every region you accept clients from.
                </p>

                <div className="mt-4 grid gap-5 md:grid-cols-2">
                  <div ref={setRef("licensed_country")}>
                    <label className={labelCls}>Country of practice (primary)</label>
                    <select
                      className={inputCls("licensed_country")}
                      value={form.licensed_country}
                      onChange={(e) => {
                        const c = e.target.value;
                        setForm({ ...form, licensed_country: c });
                        if (c !== "US") setLicensedStates([]);
                      }}
                    >
                      <option value="">Select country…</option>
                      {COUNTRY_OPTIONS.map((c) => (
                        <option key={c.code} value={c.code}>{c.name}</option>
                      ))}
                      <option value="OTHER">🌍 Other country / region…</option>
                    </select>
                    {form.licensed_country === "OTHER" && (
                      <input
                        className={`${inputCls()} mt-2`}
                        placeholder="Please specify your country or jurisdiction"
                        value={form.licensed_country_other}
                        onChange={(e) => setForm({ ...form, licensed_country_other: e.target.value })}
                      />
                    )}
                  </div>

                  <div>
                    <label className={labelCls}>
                      Additional countries / regions <span className="font-normal text-muted-foreground">(optional)</span>
                    </label>
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        list="country-options-list"
                        className={`${inputCls()} min-w-[180px] flex-1`}
                        placeholder="Type a country and press Add…"
                        value={jurisdictionDraft}
                        onChange={(e) => setJurisdictionDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            const v = jurisdictionDraft.trim();
                            if (!v) return;
                            const canonical = countryNameFromCode(countryCodeFromName(v)) || v;
                            if (!additionalJurisdictions.includes(canonical)) {
                              setAdditionalJurisdictions((arr) => [...arr, canonical]);
                            }
                            setJurisdictionDraft("");
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="rounded-full border border-gold/40 px-3 py-2 text-sm text-foreground hover:bg-gold/10"
                        onClick={() => {
                          const v = jurisdictionDraft.trim();
                          if (!v) return;
                          const canonical = countryNameFromCode(countryCodeFromName(v)) || v;
                          if (!additionalJurisdictions.includes(canonical)) {
                            setAdditionalJurisdictions((arr) => [...arr, canonical]);
                          }
                          setJurisdictionDraft("");
                        }}
                      >
                        + Add
                      </button>
                    </div>
                    {additionalJurisdictions.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {additionalJurisdictions.map((j) => (
                          <span
                            key={j}
                            className="inline-flex items-center gap-1 rounded-full border border-gold/30 bg-gold/10 px-3 py-1 text-sm text-foreground"
                          >
                            {j}
                            <button
                              type="button"
                              aria-label={`Remove ${j}`}
                              className="ms-1 text-muted-foreground hover:text-foreground"
                              onClick={() =>
                                setAdditionalJurisdictions((arr) => arr.filter((x) => x !== j))
                              }
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {form.licensed_country === "US" && (
                  <div ref={setRef("licensed_states")} className="mt-5">
                    <label className={labelCls}>Licensed state(s)</label>
                    <p className={hintCls}>
                      Select every US state where you hold a valid licence — you'll only be visible
                      to users in these states.
                    </p>
                    <div
                      className={`mt-2 flex flex-wrap gap-2 ${
                        missingKeys.has("licensed_states")
                          ? "rounded-lg p-2 ring-2 ring-destructive/40"
                          : ""
                      }`}
                    >
                      {US_STATE_OPTIONS.map((s) => (
                        <button
                          type="button"
                          key={s.code}
                          onClick={() => toggle(licensedStates, s.code, setLicensedStates)}
                          className={`rounded-full border px-3 py-1 text-xs ${
                            licensedStates.includes(s.code)
                              ? "border-gold bg-gold/20 text-foreground"
                              : "border-gold/30 text-muted-foreground hover:bg-gold/10"
                          }`}
                        >
                          {s.code}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <label className="mt-5 flex items-start gap-3 rounded-xl border border-gold/25 bg-card p-3 text-sm text-foreground">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={form.telehealth_permitted}
                    onChange={(e) => setForm({ ...form, telehealth_permitted: e.target.checked })}
                  />
                  <span>
                    Telehealth is permitted under my licence
                    <span className="block text-xs text-muted-foreground">
                      For the regions listed above.
                    </span>
                  </span>
                </label>
              </div>
            </StepShell>
          )}

          {/* ───────────── Step 3: Your Practice ───────────── */}
          {step === 3 && (
            <StepShell
              icon={Globe2}
              title="Your practice"
              blurb="How you work, who you help, and how clients reach you."
            >
              <div className="space-y-6">
                <div>
                  <label className={labelCls}>Therapy approaches</label>
                  <ChipPicker
                    options={APPROACHES}
                    selected={approaches}
                    onToggle={(a) => toggle(approaches, a, setApproaches)}
                    placeholder="Search approaches…"
                  />
                  {approaches.includes("Other") && (
                    <input
                      className={`${inputCls()} mt-3`}
                      placeholder="e.g. ACT, Compassion Focused Therapy, Gestalt"
                      value={approachesOther}
                      onChange={(e) => setApproachesOther(e.target.value)}
                      maxLength={300}
                    />
                  )}
                </div>

                <div>
                  <label className={labelCls}>Specialisms</label>
                  <ChipPicker
                    options={SPECIALISMS}
                    selected={specialisms}
                    onToggle={(a) => toggle(specialisms, a, setSpecialisms)}
                    placeholder="Search specialisms…"
                  />
                  {specialisms.includes("Other") && (
                    <input
                      className={`${inputCls()} mt-3`}
                      placeholder="e.g. Perinatal trauma, Medical trauma"
                      value={specialismsOther}
                      onChange={(e) => setSpecialismsOther(e.target.value)}
                      maxLength={300}
                    />
                  )}
                </div>

                <div>
                  <label className={labelCls}>Languages</label>
                  <ChipPicker
                    options={LANGUAGES}
                    selected={languages}
                    onToggle={(a) => toggle(languages, a, setLanguages)}
                    placeholder="Search languages…"
                  />
                  {languages.includes("Other") && (
                    <input
                      className={`${inputCls()} mt-3`}
                      placeholder="e.g. Polish, Hindi, Tagalog"
                      value={languagesOther}
                      onChange={(e) => setLanguagesOther(e.target.value)}
                      maxLength={300}
                    />
                  )}
                </div>

                <div>
                  <label className={labelCls}>Availability</label>
                  <div className="flex flex-wrap gap-2">
                    {([
                      { key: "online", label: "Online" },
                      { key: "in_person", label: "In-person" },
                    ] as const).map((o) => (
                      <button
                        type="button"
                        key={o.key}
                        onClick={() => setForm({ ...form, [o.key]: !form[o.key] })}
                        aria-pressed={form[o.key]}
                        className={`rounded-full border px-4 py-2 text-sm ${
                          form[o.key]
                            ? "border-gold bg-gold/20 text-foreground"
                            : "border-gold/30 text-muted-foreground hover:bg-gold/10"
                        }`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div ref={setRef("website")}>
                  <label className={labelCls}>Booking or contact link</label>
                  <input
                    type="url"
                    className={inputCls("website")}
                    placeholder="https://your-website-or-booking-page.com"
                    value={form.website}
                    onChange={(e) => setForm({ ...form, website: e.target.value })}
                  />
                  <p className={hintCls}>
                    Shown publicly on your profile. A website, booking page, directory profile or
                    contact page all work.
                  </p>
                </div>
              </div>
            </StepShell>
          )}

          {/* ───────────── Step 4: Verification ───────────── */}
          {step === 4 && (
            <StepShell
              icon={ShieldCheck}
              title="Verification"
              blurb="Documents are used for verification only and are never shown publicly."
            >
              <div className="rounded-2xl border border-gold/25 bg-background/60 p-4 text-sm text-foreground/80">
                Uploads are optional here — if anything fails, email your documents to{" "}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[var(--gold-deep)] underline underline-offset-2">
                  {SUPPORT_EMAIL}
                </a>{" "}
                and we'll attach them for you.
              </div>

              <div className="mt-6 space-y-6">
                <UploadField
                  id="photo"
                  label="Profile photo"
                  guidance="JPG, PNG or WebP – max 5 MB. Resized on your device for a faster upload."
                  accept="image/png,image/jpeg,image/webp"
                  promptText="Choose a photo"
                  state={uploads.photo}
                  isInvalid={missingKeys.has("photo")}
                  sectionRef={setRef("photo")}
                  uploadCls={uploadCls("photo")}
                  onSelect={(f) => startUpload("photo", f, UPLOAD_CONFIGS.photo)}
                  onRetry={() => {
                    const s = uploads.photo;
                    if (s.status === "error") startUpload("photo", s.file, s.opts);
                  }}
                  onClear={() => clearUpload("photo")}
                />

                <UploadField
                  id="certificate"
                  label="Qualification / licence certificate"
                  guidance="PDF, JPG or PNG – max 10 MB. Verification only, never public."
                  accept="application/pdf,image/png,image/jpeg,image/webp"
                  promptText="Choose certificate"
                  state={uploads.certificate}
                  isInvalid={missingKeys.has("certificate")}
                  sectionRef={setRef("certificate")}
                  uploadCls={uploadCls("certificate")}
                  onSelect={(f) => startUpload("certificate", f, UPLOAD_CONFIGS.certificate)}
                  onRetry={() => {
                    const s = uploads.certificate;
                    if (s.status === "error") startUpload("certificate", s.file, s.opts);
                  }}
                  onClear={() => clearUpload("certificate")}
                />

                <UploadField
                  id="insurance"
                  label="Insurance certificate"
                  guidance="PDF, JPG or PNG – max 10 MB. Verification only, never public."
                  accept="application/pdf,image/png,image/jpeg,image/webp"
                  promptText="Choose insurance certificate"
                  state={uploads.insurance}
                  isInvalid={missingKeys.has("insurance")}
                  sectionRef={setRef("insurance")}
                  uploadCls={uploadCls("insurance")}
                  onSelect={(f) => startUpload("insurance", f, UPLOAD_CONFIGS.insurance)}
                  onRetry={() => {
                    const s = uploads.insurance;
                    if (s.status === "error") startUpload("insurance", s.file, s.opts);
                  }}
                  onClear={() => clearUpload("insurance")}
                />

                <div className="rounded-2xl border border-gold/25 bg-background/60 p-4 sm:p-5">
                  <h3 className="text-sm font-semibold text-foreground">
                    Professional indemnity / liability insurance
                  </h3>
                  <p className={hintCls}>
                    Required before publication. The certificate can follow later, but provider and
                    expiry are needed now.
                  </p>
                  <div className="mt-4 grid gap-5 md:grid-cols-2">
                    <div ref={setRef("insurance_provider")}>
                      <label htmlFor="ta_insurance_provider" className={labelCls}>Insurance provider</label>
                      <input
                        id="ta_insurance_provider"
                        className={inputCls("insurance_provider")}
                        placeholder="e.g. Howden, Balens, CPH &amp; Associates"
                        value={form.insurance_provider}
                        onChange={(e) => setForm({ ...form, insurance_provider: e.target.value })}
                      />
                    </div>
                    <div ref={setRef("insurance_expiry")}>
                      <label htmlFor="ta_insurance_expiry" className={labelCls}>Policy expiry date</label>
                      <input
                        id="ta_insurance_expiry"
                        type="date"
                        className={inputCls("insurance_expiry")}
                        value={form.insurance_expiry}
                        onChange={(e) => setForm({ ...form, insurance_expiry: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </StepShell>
          )}

          {/* ───────────── Step 5: Review & Submit ───────────── */}
          {step === 5 && (
            <StepShell
              icon={ClipboardCheck}
              title="Review & submit"
              blurb="A quick check, three confirmations, and you're done."
            >
              <div className="grid gap-3 sm:grid-cols-2">
                <SummaryCard
                  title="About you"
                  onEdit={() => goToStep(1)}
                  rows={[
                    ["Name", form.full_name],
                    ["Title", form.professional_title],
                    ["Email", form.email],
                    ["Location", [form.state_region, form.country].filter(Boolean).join(", ")],
                    ["Experience", form.years_experience ? `${form.years_experience} years` : ""],
                  ]}
                />
                <SummaryCard
                  title="Credentials"
                  onEdit={() => goToStep(2)}
                  rows={[
                    ["Licence", [form.licence_type, form.license_number].filter(Boolean).join(" · ")],
                    ["Regulator", form.regulatory_body === "Other" ? form.regulatory_body_other : form.regulatory_body],
                    ["Expiry", form.licence_expiry],
                    ["Practises in", [primaryCountryLabel, ...licensedStates, ...additionalJurisdictions].filter(Boolean).join(", ")],
                  ]}
                />
                <SummaryCard
                  title="Practice"
                  onEdit={() => goToStep(3)}
                  rows={[
                    ["Approaches", mergeOther(approaches, approachesOther).join(", ")],
                    ["Specialisms", mergeOther(specialisms, specialismsOther).join(", ")],
                    ["Languages", mergeOther(languages, languagesOther).join(", ")],
                    ["Availability", [form.online && "Online", form.in_person && "In-person"].filter(Boolean).join(", ")],
                    ["Link", form.website],
                  ]}
                />
                <SummaryCard
                  title="Verification"
                  onEdit={() => goToStep(4)}
                  rows={[
                    ["Photo", uploads.photo.status === "done" ? "Uploaded" : "To follow by email"],
                    ["Certificate", uploads.certificate.status === "done" ? "Uploaded" : "To follow by email"],
                    ["Insurance doc", uploads.insurance.status === "done" ? "Uploaded" : "To follow by email"],
                    ["Insurer", form.insurance_provider],
                    ["Policy expiry", form.insurance_expiry],
                  ]}
                />
              </div>

              <div className="mt-6 space-y-3">
                <h3 className="text-sm font-semibold text-foreground">Final agreement</h3>
                <ConsentRow
                  refFn={setRef("confirm_accuracy")}
                  invalid={missingKeys.has("confirm_accuracy")}
                  checked={form.confirm_accuracy}
                  onChange={(v) => setForm({ ...form, confirm_accuracy: v })}
                >
                  I confirm that the information provided is accurate and that I hold the
                  licences/registrations, insurance and legal authority required for the services
                  and jurisdictions listed in my application.
                </ConsentRow>
                <ConsentRow
                  refFn={setRef("confirm_terms")}
                  invalid={missingKeys.has("confirm_terms")}
                  checked={form.confirm_terms}
                  onChange={(v) => setForm({ ...form, confirm_terms: v })}
                >
                  I have read and agree to the{" "}
                  <a href="/therapist-terms" target="_blank" rel="noopener noreferrer" className="text-[var(--gold-deep)] underline">
                    Therapist Membership Terms &amp; Conditions
                  </a>{" "}
                  and{" "}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[var(--gold-deep)] underline">
                    Privacy Policy
                  </a>
                  , and understand that Recovery Trauma™ operates as a directory and does not
                  employ, supervise or manage independent therapists.
                </ConsentRow>
                <ConsentRow
                  refFn={setRef("confirm_publish")}
                  invalid={missingKeys.has("confirm_publish")}
                  checked={form.confirm_publish}
                  onChange={(v) => setForm({ ...form, confirm_publish: v })}
                >
                  I consent to my approved professional profile being published in the Recovery
                  Trauma™ Therapist Directory.
                </ConsentRow>

                <div className="pt-1">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Optional
                  </p>
                  <ConsentRow
                    checked={form.marketing_consent}
                    onChange={(v) => setForm({ ...form, marketing_consent: v })}
                  >
                    I would like to receive therapist opportunities, platform updates, workshops and
                    other relevant communications from Recovery Trauma™.
                  </ConsentRow>
                </div>
              </div>

              {(isSubmitting || mutation.isError) && (
                <div
                  role="status"
                  aria-live="polite"
                  className={`mt-6 flex items-start gap-3 rounded-xl border p-4 text-sm font-medium ${
                    isSubmitting
                      ? "border-gold/40 bg-gold/10 text-foreground"
                      : "border-destructive/40 bg-destructive/10 text-foreground"
                  }`}
                >
                  {isSubmitting ? (
                    <Loader2 className="mt-0.5 h-5 w-5 shrink-0 animate-spin text-gold" />
                  ) : (
                    <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
                  )}
                  <div>
                    <p className="font-semibold">
                      {isSubmitting ? "Submitting application…" : "Submission failed"}
                    </p>
                    <p className="mt-1 font-normal text-muted-foreground">
                      {isSubmitting
                        ? "Saving your application…"
                        : `${GENERIC_ERROR} Your uploaded documents are saved — you can press Submit again without re-uploading.`}
                    </p>
                  </div>
                </div>
              )}
            </StepShell>
          )}

          {/* Navigation */}
          <div className="mt-8 flex flex-col-reverse gap-3 border-t border-gold/20 pt-6 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={() => goToStep(Math.max(1, step - 1))}
              disabled={step === 1}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-gold/40 px-5 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-gold/10 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>

            {step < 5 ? (
              <button
                type="button"
                onClick={handleNext}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[var(--gold-deep)] px-6 py-3 text-sm font-semibold text-background shadow-[var(--shadow-gold)] transition-opacity hover:opacity-90"
              >
                Continue <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                type="submit"
                disabled={isSubmitting || isUploadingAny}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[var(--gold-deep)] px-6 py-3 text-sm font-semibold text-background shadow-[var(--shadow-gold)] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {(isSubmitting || isUploadingAny) && <Loader2 className="h-4 w-4 animate-spin" />}
                {isUploadingAny
                  ? "Uploading documents…"
                  : isSubmitting
                    ? "Submitting…"
                    : "Submit application"}
              </button>
            )}
          </div>
        </form>

        <p className="mt-4 text-center text-xs text-muted-foreground">
          Applications go through document verification only. Listed profiles appear in the
          directory with a Recovery Trauma™ Documents Verified badge. Verification is not a clinical
          assessment, recommendation or endorsement.
        </p>
      </div>
    </div>
  );
}

function StepShell({
  icon: Icon,
  title,
  blurb,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  blurb: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-6 flex items-start gap-3">
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gold-light/60 text-[var(--gold-deep)]">
          <Icon className="h-5 w-5" />
        </span>
        <div>
          <h2 className="font-serif text-xl text-foreground">{title}</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">{blurb}</p>
        </div>
      </div>
      {children}
    </section>
  );
}

function SummaryCard({
  title,
  rows,
  onEdit,
}: {
  title: string;
  rows: [string, string][];
  onEdit: () => void;
}) {
  return (
    <div className="rounded-2xl border border-gold/25 bg-background/60 p-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-foreground">{title}</h4>
        <button
          type="button"
          onClick={onEdit}
          className="text-xs font-medium text-[var(--gold-deep)] underline-offset-2 hover:underline"
        >
          Edit
        </button>
      </div>
      <dl className="mt-2 space-y-1.5 text-xs">
        {rows.map(([k, v]) => (
          <div key={k} className="flex gap-2">
            <dt className="w-24 shrink-0 text-muted-foreground">{k}</dt>
            <dd className="min-w-0 flex-1 break-words text-foreground/90">
              {v?.trim() ? v : <span className="text-muted-foreground">—</span>}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function ConsentRow({
  children,
  checked,
  onChange,
  refFn,
  invalid,
}: {
  children: React.ReactNode;
  checked: boolean;
  onChange: (v: boolean) => void;
  refFn?: (el: HTMLElement | null) => void;
  invalid?: boolean;
}) {
  return (
    <label
      ref={refFn}
      className={`flex items-start gap-3 rounded-xl border p-3.5 text-sm leading-relaxed text-foreground transition-colors ${
        invalid
          ? "border-destructive bg-destructive/5 ring-2 ring-destructive/30"
          : checked
            ? "border-gold/50 bg-gold/10"
            : "border-gold/25 bg-background/60 hover:bg-gold/5"
      }`}
    >
      <input
        type="checkbox"
        className="mt-1 h-4 w-4 shrink-0 accent-[var(--gold-deep)]"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>{children}</span>
    </label>
  );
}

function Disclosure({
  title,
  summary,
  children,
}: {
  title: string;
  summary: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-gold/25 bg-card p-4 sm:p-5">
      <div className="flex items-start gap-3">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[var(--gold-deep)]" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">{title}</p>
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{summary}</p>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-[var(--gold-deep)] underline-offset-2 hover:underline"
          >
            {open ? "Show less" : "Learn more"}
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
          {open && (
            <div className="mt-3 rounded-xl border border-gold/20 bg-background/60 p-3 text-xs leading-relaxed text-foreground/80">
              {children}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function UploadField({
  id,
  label,
  guidance,
  accept,
  promptText,
  state,
  isInvalid,
  sectionRef,
  uploadCls,
  onSelect,
  onRetry,
  onClear,
}: {
  id: string;
  label: string;
  guidance: string;
  accept: string;
  promptText: string;
  state: UploadState;
  isInvalid: boolean;
  sectionRef: (el: HTMLElement | null) => void;
  uploadCls: string;
  onSelect: (f: File) => void;
  onRetry: () => void;
  onClear: () => void;
}) {
  const hasFile = state.status !== "idle";
  const displayName = state.status === "idle" ? promptText : state.fileName;
  return (
    <section ref={sectionRef}>
      <label className="mb-1 block text-sm font-medium text-foreground" htmlFor={`upload-${id}`}>
        {label}
      </label>
      <p className="mb-2 text-xs text-muted-foreground">{guidance}</p>
      <label className={uploadCls} htmlFor={`upload-${id}`}>
        <Upload className="h-4 w-4 shrink-0" />
        <span className="truncate">{displayName}</span>
        {hasFile && (
          <span className="ms-auto shrink-0 text-xs text-muted-foreground">
            {formatBytes(state.sizeBytes)}
          </span>
        )}
        <input
          id={`upload-${id}`}
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.currentTarget.value = "";
            if (f) onSelect(f);
          }}
        />
      </label>
      <UploadStatusRow state={state} onRetry={onRetry} onClear={onClear} />
      {isInvalid && state.status !== "done" && (
        <p className="mt-1 text-xs text-destructive">Please upload this file before submitting.</p>
      )}
    </section>
  );
}

function UploadStatusRow({
  state,
  onRetry,
  onClear,
}: {
  state: UploadState;
  onRetry: () => void;
  onClear: () => void;
}) {
  if (state.status === "idle") return null;
  if (state.status === "compressing") {
    return (
      <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-muted-foreground" aria-live="polite">
        <Loader2 className="h-3.5 w-3.5 animate-spin text-gold" />
        Preparing file…
      </p>
    );
  }
  if (state.status === "uploading") {
    return (
      <div className="mt-2 space-y-1" aria-live="polite">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <Loader2 className="h-3.5 w-3.5 animate-spin text-gold" />
            Uploading {formatBytes(state.sizeBytes)}…
          </span>
          <span className="font-medium text-foreground">{state.progress}%</span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-gold/15">
          <div
            className="h-full bg-gold transition-[width] duration-200"
            style={{ width: `${state.progress}%` }}
          />
        </div>
      </div>
    );
  }
  if (state.status === "done") {
    return (
      <div className="mt-2 flex items-center justify-between gap-2 text-xs">
        <span className="inline-flex items-center gap-1.5 font-medium text-emerald-600">
          <CheckCircle2 className="h-3.5 w-3.5" /> Uploaded ✓
        </span>
        <button
          type="button"
          onClick={onClear}
          className="text-muted-foreground underline-offset-2 hover:underline"
        >
          Replace file
        </button>
      </div>
    );
  }
  return (
    <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs">
      <span className="inline-flex items-center gap-1.5 font-medium text-destructive">
        <X className="h-3.5 w-3.5" /> {state.message}
      </span>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onRetry}
          className="font-medium text-foreground underline underline-offset-2 hover:opacity-80"
        >
          Try again
        </button>
        <button
          type="button"
          onClick={onClear}
          className="text-muted-foreground underline-offset-2 hover:underline"
        >
          Choose different file
        </button>
      </div>
    </div>
  );
}

function ChipPicker({
  options,
  selected,
  onToggle,
  placeholder,
  alwaysShow = ["Other"],
  collapseAfter = 8,
}: {
  options: string[];
  selected: string[];
  onToggle: (value: string) => void;
  placeholder?: string;
  alwaysShow?: string[];
  collapseAfter?: number;
}) {
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(false);
  const q = query.trim().toLowerCase();
  const selectedSet = new Set(selected);

  const selectedOptions = options.filter((o) => selectedSet.has(o));

  let unselected = options.filter((o) => !selectedSet.has(o));
  if (q) {
    unselected = unselected.filter(
      (o) => o.toLowerCase().includes(q) || alwaysShow.includes(o),
    );
  } else if (!expanded && unselected.length > collapseAfter) {
    const keep = unselected.filter((o) => alwaysShow.includes(o));
    const head = unselected.filter((o) => !alwaysShow.includes(o)).slice(0, collapseAfter);
    unselected = [...head, ...keep];
  }

  const visible = [...selectedOptions, ...unselected];
  const hiddenCount = !q && !expanded
    ? Math.max(0, options.length - selectedOptions.length - unselected.length)
    : 0;

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          inputMode="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder ?? "Search…"}
          className="w-full rounded-full border border-gold/30 bg-background/60 py-2 ps-9 pe-9 text-sm text-foreground placeholder:text-muted-foreground focus:border-gold focus:outline-none"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery("")}
            aria-label="Clear search"
            className="absolute end-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {visible.map((a) => (
          <button
            type="button"
            key={a}
            onClick={() => onToggle(a)}
            className={`rounded-full border px-3 py-1.5 text-sm ${
              selectedSet.has(a)
                ? "border-gold bg-gold/20 text-foreground"
                : "border-gold/30 text-muted-foreground hover:bg-gold/10"
            }`}
          >
            {a}
          </button>
        ))}
        {visible.length === 0 && (
          <p className="text-xs text-muted-foreground">No matches. Choose "Other" to add a custom entry.</p>
        )}
      </div>
      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="text-xs font-medium text-[var(--gold-deep)] underline-offset-2 hover:underline"
        >
          Show {hiddenCount} more
        </button>
      )}
      {expanded && !q && (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="text-xs font-medium text-muted-foreground underline-offset-2 hover:underline"
        >
          Show less
        </button>
      )}
    </div>
  );
}
